import type { QueryClient } from '@tanstack/svelte-query';
import { createQuery } from '@tanstack/svelte-query';
import { fromStore, get, toStore, type Readable } from 'svelte/store';
import type {
	GetPrimaryCartQuery,
	GetPrimaryCartQueryVariables,
} from '../../queries/__generated__/getPrimaryCart.generated';
import { GetPrimaryCartDocument } from '../../queries/__generated__/getPrimaryCart.generated';
import { getPrimaryCartQueryVariables } from '../getPrimaryCartQueryVariables/getPrimaryCartQueryVariables';
import { FeatureFlags } from '#lib/constants/feature-flags.js';
import { NONEXISTENT_PRODUCT_ID } from '#lib/features/checkout/constants/nonexistent-product-id.js';
import type { CartEntryItem } from '#lib/features/checkout/types.js';
import type { CartItems } from '#lib/features/checkout/utils/createCartItemsStore/index.js';
import { getQuery } from '#lib/query-utils/getQuery.js';
import { stringifyIds } from '#lib/websites/nft/utils/stringifyIds/stringifyIds.js';
import { getPrimaryCart } from '#lib/websites/primary/api/getPrimaryCart.js';
import { getQueryKeys } from '$global/query-utils/getQueryKeys';
import { getImageUrl } from '$global/utils/getImageUrl';

export const getPrimaryCartQuery = (
	queryClient: QueryClient,
	cartItems: Readable<CartItems | undefined>
) => {
	const cartItems$ = fromStore(cartItems);

	return createQuery(() => {
		const $cartItems = cartItems$.current;
		const ids = Object.keys($cartItems || {});
		const queryClientData = queryClient.getQueriesData({});

		const existingQueryResult = ids?.length
			? queryClientData?.find(([[key, _, variables]]) => {
					return (
						key === 'getPrimaryCart' &&
						ids.every((id) =>
							(
								variables as GetPrimaryCartQueryVariables
							)?.filter?.product?.id?.['_in']?.includes(id)
						)
					);
				})
			: null;

		// If there's already cache entry a query fetching more editions than the current one
		if (existingQueryResult?.[1]) {
			return {
				queryKey: getQueryKeys(
					GetPrimaryCartDocument,
					getPrimaryCartQueryVariables(ids)
				),
				queryFn: () => {
					return {
						Primary_Artworks: (
							existingQueryResult?.[1] as GetPrimaryCartQuery
						).Primary_Artworks?.filter((artwork) =>
							ids.includes(artwork.product?.id as string)
						),
						Primary_Generic_Artwork: (
							existingQueryResult?.[1] as GetPrimaryCartQuery
						).Primary_Generic_Artwork?.filter((artwork) =>
							ids.includes(artwork.product?.id as string)
						),
					};
				},
			};
		}

		const queryIds = ids?.length ? ids : [NONEXISTENT_PRODUCT_ID];
		const variables = getPrimaryCartQueryVariables(queryIds);

		// The query key is the GraphQL one on both paths, so the cache reuse above
		// and the layout's empty-cart seed keep working whichever path is live.
		return FeatureFlags.RestPages['Primary cart and wishlist hydration']
			? {
					queryKey: getQueryKeys(GetPrimaryCartDocument, variables),
					queryFn: async (): Promise<GetPrimaryCartQuery> =>
						stringifyIds(await getPrimaryCart(fetch, queryIds)),
				}
			: getQuery(GetPrimaryCartDocument, variables);
	});
};

export const formatPrimaryItems = (
	$primaryCartQuery: {
		data: GetPrimaryCartQuery | undefined;
	},
	cartItems: Readable<CartItems | undefined>
): CartEntryItem[] => [
	...($primaryCartQuery.data?.Primary_Generic_Artwork || []).map(
		(artwork): CartEntryItem => {
			return {
				image: null,
				line1: artwork?.artists
					?.map((artist) => artist?.Artists_id?.name)
					?.join(', '),
				line2: artwork?.title,
				line3: artwork?.dimensions,
				productId: artwork?.product?.id,
				id: artwork?.id,
				quantity:
					get(cartItems)?.[artwork?.product?.id as string]?.quantity || 0,
				currency: artwork?.product?.currency,
				price: artwork?.product?.price,
				sku: artwork?.product?.sku,
				variant: null,
				groupId: artwork?.collection?.code,
				groupName: artwork?.collection?.name,
				dropConfigId: artwork?.collection?.drop_page?.drop_config?.[0]?.id,
				hidePrices:
					artwork?.collection?.drop_page?.drop_config?.[0]?.hide_prices,
			};
		}
	),
	...($primaryCartQuery.data?.Primary_Artworks || [])
		.sort((artworkA, artworkB) => {
			const findArtworkSortIndex = (artwork: typeof artworkA) =>
				Object.values(get(cartItems) || {}).find(
					(cartItem) => cartItem.name === artwork.artwork_title
				)?.sort || 0;

			return findArtworkSortIndex(artworkA) - findArtworkSortIndex(artworkB);
		})
		.map((artwork): CartEntryItem => {
			const allDefinedImage = artwork.artwork_featured_image
				? artwork.artwork_featured_image
				: null;

			return {
				image: allDefinedImage
					? {
							width: allDefinedImage?.width || 0,
							height: allDefinedImage?.height || 0,
							url: `${getImageUrl(allDefinedImage?.filename_disk, '512px')}`,
						}
					: null,
				line1: artwork?.artists
					?.map((artist) => artist?.Artists_id?.name)
					?.join(', '),
				line2: [artwork?.artwork_code, artwork?.artwork_title]
					.filter(Boolean)
					.join(' | '),
				line3:
					artwork.artwork_dimensions ||
					`${artwork?.artwork_height_cm} x ${artwork?.artwork_width_cm} CM`,
				productId: artwork?.product?.id,
				id: artwork?.id,
				quantity:
					get(cartItems)?.[artwork?.product?.id as string]?.quantity || 0,
				currency: artwork?.product?.currency,
				sku: artwork?.product?.sku,
				price: artwork?.product?.price,
				variant: null,
				groupId: artwork?.primary_collection?.code,
				groupName: artwork?.primary_collection?.name,
				stockLimited: true,
				dropConfigId:
					artwork?.primary_collection?.drop_page?.drop_config?.[0]?.id,
				hidePrices:
					artwork?.primary_collection?.drop_page?.drop_config?.[0]?.hide_prices,
			};
		}),
];

export const createPrimaryCartQuery = (
	queryClient: QueryClient,
	cartItems: Readable<CartItems | undefined>,
	defaultLoading?: boolean
) => {
	const primaryCartQuery = getPrimaryCartQuery(queryClient, cartItems);

	return toStore(() => ({
		...primaryCartQuery,
		error: primaryCartQuery.error || null,
		data: primaryCartQuery.data || {
			Primary_Artworks: [],
			Primary_Generic_Artwork: [],
		},
		isLoading: (() => {
			if (!primaryCartQuery.data) {
				return typeof defaultLoading !== 'undefined' ? defaultLoading : false;
			}

			return primaryCartQuery.isPending;
		})(),
		subtotal: Object.values(get(cartItems) || {}).reduce(
			(accumulator, cartItem) => {
				if (!cartItem.quantity || !cartItem.price) {
					return accumulator;
				}

				return accumulator + cartItem.quantity * cartItem.price;
			},
			0
		),
		formattedItems: formatPrimaryItems(primaryCartQuery, cartItems),
	}));
};
